import { database } from '@app/firebase';
import HomeCard from '@components/cards/HomeCard';
import HomeCardWide from '@components/cards/HomeCardWide';
import ProductCard from '@components/cards/ProductCard';
import RoomCard from '@components/cards/RoomCard';
import PhotosCarousel from '@components/carousels/PhotosCarousel';
import FancyInput from '@components/fancy-input/FancyInput';
import FancyInputDouble from '@components/fancy-input/FancyInputDouble';
import FancyLocationSearch from '@components/fancy-input/FancyLocationSearch';
import FancyPhoneInput from '@components/fancy-input/FancyPhoneInput';
import awaitUserLogin from '@components/login/awaitUserLogin';
import Testimonials from '@components/testimonials/Testimonials';
import UserContext from '@context/UserContext';
import useCollection from '@hooks/useCollection';
import { ContentBlock } from '@layouts/ContentBlocks';
import IconStarBorder from '@mui/icons-material/StarBorder';
import Box from '@mui/material/Box';
import Button, { ButtonProps } from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import SubmitButton from '@ui/components/buttons/SubmitButton';
import IconEyeClosed from '@ui/icons/imaterial/base/Eye Crossed.svg';
import IconEyeOpen from '@ui/icons/imaterial/base/Eye Open.svg';
import IconMailClosed from '@ui/icons/imaterial/communication/Mail Closed.svg';
import makeStyles from '@ui/utils/makeStyles';
import onSavedClickRoom from '@utils/clickHandlers/onSavedClickRoom';
import properCase from '@utils/properCase';
import { addSavedHome, removeSavedHome } from '@utils/savedItems';
import { collection, collectionGroup, limit, query } from 'firebase/firestore';
import formatCurrency from 'format-currency';
import React, { useContext, useMemo, useState } from 'react';

import { DefaultFirestoreConverter } from '../../../types/DefaultFirestoreConverter';

const useStyles = makeStyles()((theme) => ({
  buttonGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridGap: theme.spacing(4),
    alignItems: 'center',
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: '1fr 1fr 1fr',
    }
  }
}));


function TextFields() {
  const { classes } = useStyles();
  const [showPassword, setShowPassword] = useState(false);
  const toggleShowPassword = () => setShowPassword(!showPassword);
  const variants: Array<'filled' | 'outlined' | 'standard'> = ['filled', 'outlined', 'standard'];
  const sizes: Array<'small' | 'medium'> = ['small', 'medium'];

  return (
    <ContentBlock margin={[6, 'auto']}>
      {
        variants.map(variant => {
          return sizes.map(size => {
            return (
              <React.Fragment key={`${variant}-${size}`}>
                <Typography variant="h3">
                  {properCase([variant, size].join(' '))}
                </Typography>
                <ContentBlock
                  key={variant}
                  margin={[4, 'auto']}
                  className={classes.buttonGrid}
                >
                  <TextField
                    required
                    size={size}
                    label="Required"
                    defaultValue="Hello World"
                    variant={variant}
                  />
                  <TextField
                    disabled
                    size={size}
                    label="Disabled"
                    defaultValue="Hello World"
                    variant={variant}
                  />
                  <TextField
                    size={size}
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    variant={variant}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          style={{
                            position: 'absolute',
                            right: 16,
                          }}
                        >
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={toggleShowPassword}
                          >
                            {showPassword ? <IconEyeClosed /> : <IconEyeOpen />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      '& .MuiFilledInput-root.MuiFilledInput-underline.MuiInputBase-root.MuiInputBase-colorPrimary.MuiInputBase-fullWidth.MuiInputBase-formControl': {
                        paddingRight: 0
                      }
                    }}
                  />
                  <TextField
                    size={size}
                    label="Read Only"
                    defaultValue="Hello World"
                    InputProps={{
                      readOnly: true,
                    }}
                    variant={variant}
                  />
                  <TextField
                    size={size}
                    label="Number"
                    type="number"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    variant={variant}
                  />
                  <TextField
                    size={size}
                    label="Search field"
                    type="search"
                    variant={variant}
                  />
                  <TextField
                    size={size}
                    label="Helper text"
                    defaultValue="Default Value"
                    helperText="Some important text"
                    variant={variant}
                  />
                  <TextField
                    error
                    size={size}
                    label="Error"
                    defaultValue="Default Value"
                    helperText="Some important text"
                    variant={variant}
                  />
                </ContentBlock>
              </React.Fragment>
            );
          }
          );
        }
        )}
    </ContentBlock>
  );
}

type Extra = {
  href?: string;
} & Partial<ButtonProps>

function Buttons() {
  const { classes } = useStyles();
  const sizes: Array<'small' | 'medium' | 'large'> = ['small', 'medium', 'large'];
  const variants: Array<'contained' | 'outlined' | 'text'> = ['contained', 'outlined', 'text'];
  const extra: Extra[] = [
    {
      color: 'primary',
      startIcon: <IconStarBorder />
    },
    {
      color: 'secondary',
      startIcon: <IconStarBorder />
    },
    {
      color: 'white',
      startIcon: <IconStarBorder />
    },
    {
      color: 'info',
      startIcon: <IconStarBorder />
    },
    {
      color: 'primary',
      disabled: true,
      startIcon: <IconStarBorder />
    },
    {
      color: 'secondary',
      disabled: true,
      startIcon: <IconStarBorder />
    },
  ];

  return (
    <ContentBlock margin={[8, 'auto', 4]}>
      <div className={classes.buttonGrid}>
        {extra.map((extra, h) => {
          return variants.map((variant, j) => {
            return sizes.map((size, i) => {
              const details = { size, variant, ...extra };

              return (
                <Tooltip
                  key={`${h}:${i}:${j}`}
                  title={(
                    <>
                      {Object.keys(details)
                        .filter(k => k !== 'startIcon')
                        .map(k => (
                          <div key={k}>
                            {k}
                            :
                            {' '}
                            {details[k]}
                          </div>
                        ))}
                    </>
                  )}
                >
                  <Button
                    variant={variant}
                    size={size}
                    {...extra}
                  >
                    {properCase(Object.keys({ variant, color: extra.color }).map(k => [details[k]]).join(' '))}
                  </Button>
                </Tooltip>
              );
            });
          });
        })}

        {sizes.map((size, i) => {
          const details = { size, ...extra };

          return (
            <Tooltip
              key={i}
              title={(
                <>
                  {Object.keys(details)
                    .filter(k => k !== 'startIcon')
                    .map(k => (
                      <div key={k}>
                        {k}
                        :
                        {' '}
                        {details[k]}
                      </div>
                    ))}
                </>
              )}
            >
              <SubmitButton
                isSubmitting
                variant="contained"
                color="secondary"
                size={size}
                {...extra}
              >
                {properCase(Object.keys({ variant: 'contained', color: 'secondary' }).map(k => [details[k]]).join(' '))}
              </SubmitButton>
            </Tooltip>
          );
        })}

      </div>
    </ContentBlock>
  );
}

function Type() {

  return (
    <ContentBlock
      margin={[8, 'auto', 4]}
      style={{
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Typography
        gutterBottom
        variant="display1"
      >
        Display 1
      </Typography>
      <Typography
        gutterBottom
        variant="display2"
      >
        Display 2
      </Typography>
      <Typography
        gutterBottom
        variant="display3"
      >
        Display 3
      </Typography>
      <Typography
        gutterBottom
        variant="display4"
      >
        Display 4
      </Typography>
      <Typography
        gutterBottom
        variant="h1"
      >
        Heading Level 1
      </Typography>

      <Typography
        gutterBottom
        variant="h2"
      >
        Heading Level 2
      </Typography>

      <Typography
        gutterBottom
        variant="h3"
      >
        Heading Level 3
      </Typography>

      <Typography
        gutterBottom
        variant="h4"
      >
        Heading Level 4
      </Typography>

      <Typography
        gutterBottom
        variant="h5"
      >
        Heading Level 5
      </Typography>

      <Typography
        gutterBottom
        variant="h6"
      >
        Heading Level 6
      </Typography>

      <Typography
        gutterBottom
        variant="subtitle1"
      >
        subtitle1. Lorem ipsum dolor sit amet, consectetur adipisicing elit. Quos blanditiis tenetur
      </Typography>

      <Typography
        gutterBottom
        variant="subtitle2"
      >
        subtitle2. Lorem ipsum dolor sit amet, consectetur adipisicing elit. Quos blanditiis tenetur
      </Typography>

      <Typography
        gutterBottom
        paragraph
        variant="bodyLarge"
      >
        bodyLarge. Lorem ipsum dolor sit amet, consectetur adipisicing elit. Quos blanditiis tenetur
        unde suscipit, quam beatae rerum inventore consectetur, neque doloribus, cupiditate numquam
        dignissimos laborum fugiat deleniti? Eum quasi quidem quibusdam.
      </Typography>

      <Typography
        gutterBottom
        paragraph
        variant="bodyMedium"
      >
        bodyMedium. Lorem ipsum dolor sit amet, consectetur adipisicing elit. Quos blanditiis tenetur
        unde suscipit, quam beatae rerum inventore consectetur, neque doloribus, cupiditate numquam
        dignissimos laborum fugiat deleniti? Eum quasi quidem quibusdam.
      </Typography>

      <Typography
        gutterBottom
        paragraph
        variant="bodySmall"
      >
        bodySmall. Lorem ipsum dolor sit amet, consectetur adipisicing elit. Quos blanditiis tenetur
        unde suscipit, quam beatae rerum inventore consectetur, neque doloribus, cupiditate numquam
        dignissimos laborum fugiat deleniti? Eum quasi quidem quibusdam.
      </Typography>

      <Typography
        gutterBottom
        variant="button"
        display="block"
      >
        Button CTA
      </Typography>

      <Typography
        gutterBottom
        variant="buttonSmall"
        display="block"
      >
        Button CTA Small
      </Typography>

      <Typography
        gutterBottom
        variant="caption1"
        display="block"
      >
        Caption1 Text
      </Typography>

      <Typography
        gutterBottom
        variant="caption2"
        display="block"
      >
        Caption2 Text
      </Typography>

      <Typography
        gutterBottom
        variant="overline"
        display="block"
      >
        Overline Text
      </Typography>

      <Typography
        gutterBottom
        variant="linkedText"
        display="block"
      >
        Linked Text
      </Typography>

      <Typography
        gutterBottom
        variant="linkedTextSmall"
        display="block"
      >
        Linked Text Small
      </Typography>
    </ContentBlock>
  );
}

function Cards() {
  const userContext = useContext<UserContext>(UserContext);
  const { collection: products } = useCollection(
    query(collection(database, 'products'),
      limit(12))
  );
  const { collection: homes } = useCollection(
    query(collection(database, 'homes').withConverter(new DefaultFirestoreConverter<guesthouse.Home>()), limit(12))
  );
  const { collection: rooms } = useCollection(
    query(collectionGroup(database, 'rooms').withConverter(new DefaultFirestoreConverter<guesthouse.Room>()), limit(12))
  );

  return (
    <>
      {products?.docs && (
        <ContentBlock margin={[8, 'auto', 4]}>
          <Grid
            container
            spacing={2}
          >
            {products.docs.map((product) => {
              const productData = product.data();

              return (
                <Grid
                  key={productData.docID}
                  item
                  xs={6}
                  sm={4}
                  md={3}
                  lg={3}
                >
                  <ProductCard
                    product={productData}
                    imageHeight={productData.height}
                  />
                </Grid>
              );
            })}
          </Grid>
        </ContentBlock>
      )}


      {products?.docs && (
        <ContentBlock margin={[8, 'auto', 4]}>
          <Grid
            container
            spacing={4}
          >
            {products.docs.map((product) => {
              const productData = product.data();

              return (
                <Grid
                  key={productData.docID}
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={4}
                  xl={3}
                >
                  <ProductCard
                    product={productData}
                    imageHeight="auto"
                  />
                </Grid>
              );
            })}
          </Grid>
        </ContentBlock>
      )}

      {homes?.docs && (
        <ContentBlock margin={[8, 'auto', 4]}>
          <Grid
            container
            spacing={2}
          >
            {homes.docs.map((home) => {
              const homeData = home.data();
              const saved = userContext.savedHomes?.ids.indexOf(homeData.docID) > -1;

              return (
                <Grid
                  key={homeData.docID}
                  item
                  xs={12}
                  sm={6}
                  md={6}
                  lg={4}
                  xl={4}
                >
                  <HomeCard
                    home={homeData}
                    saved={saved}
                    style={{ margin: '0 8px 16px 8px' }}
                    renderCta={(home, className) => {
                      return (
                        <>
                          {home?.listing_price && (
                            <Typography
                              className={className}
                              component="span"
                            >
                              {`Listed for ${formatCurrency(home.listing_price, {
                                format: '%s%v',
                                symbol: '$',
                                minimumFractionDigits: 0,
                              })}`}
                            </Typography>
                          )}
                        </>
                      );
                    }}
                    onSavedClick={async () => {
                      if (userContext.user) {
                        if (saved) {
                          removeSavedHome(userContext.user.uid, homeData);
                        } else {
                          addSavedHome(userContext.user.uid, homeData);
                        }
                      } else {
                        await awaitUserLogin(userContext)
                          .then((user) => {
                            addSavedHome(user.uid, homeData);
                          });
                      }
                    }}
                  />
                </Grid>
              );
            })}
          </Grid>
        </ContentBlock>
      )}

      {rooms?.docs && (
        <ContentBlock margin={[8, 'auto', 4]}>
          <Grid
            container
            spacing={2}
          >
            {rooms.docs.map((room) => {
              const roomData = room.data();
              const saved = userContext.savedRooms?.ids.indexOf(roomData.docID) > -1;

              return (
                <Grid
                  key={roomData.docID}
                  item
                  xs={12}
                  sm={6}
                  md={6}
                  lg={4}
                  xl={4}
                >
                  <RoomCard
                    room={roomData}
                    home={roomData.home}
                    style={{ margin: '0 8px 16px 8px' }}
                    saved={saved}
                    onSavedClick={async (e) => onSavedClickRoom(userContext, saved, roomData, e)}
                  />
                </Grid>
              );
            })}
          </Grid>
        </ContentBlock>
      )}

      {homes?.docs && (
        <ContentBlock margin={[8, 'auto', 4]}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            width="100%"
          >
            {homes.docs.map((home, idx) => {
              const homeData = home.data();

              if (idx < 4) {

                return (
                  <HomeCardWide
                    key={homeData.docID}
                    home={homeData}
                    style={{ marginBottom: 64 }}
                  />
                );
              }
            })}
          </Box>
        </ContentBlock>
      )}
    </>
  );
}

function Carousels() {
  const { collection: photosCollection } = useCollection(query(collectionGroup(database, 'photos'), limit(20)));

  const photos: guesthouse.Photo[] = useMemo(() => {
    if (photosCollection?.docs?.length) {
      return photosCollection.docs.map(doc => doc.data() as guesthouse.Photo);
    }

    return [];
  }, [photosCollection]);


  return (
    <>
      <Testimonials />

      <PhotosCarousel
        photos={photos}
      />
    </>
  );
}

function FancyInputs() {
  const { theme } = useStyles();

  return (
    <ContentBlock margin={[6, 'auto']}>
      <FancyInput
        placeholder="First Name"
        style={{ margin: theme.spacing(2, 0) }}
        onSubmit={console.log}
      />

      <FancyInput
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <IconMailClosed style={{ width: 22, height: 22, position: 'relative', bottom: 2 }} />
            </InputAdornment>
          ),
        }}
        type="email"
        placeholder="Email Address"
        style={{ margin: theme.spacing(2, 0) }}
        onSubmit={console.log}
      />

      <FancyPhoneInput
        style={{ margin: theme.spacing(2, 0) }}
        onSubmit={console.log}
      />

      <FancyLocationSearch
        style={{ margin: theme.spacing(2, 0) }}
        onSubmit={console.log}
        onLocationChange={console.log}
      />

      <FancyInputDouble
        input1Props={{
          placeholder: 'First Name'
        }}
        input2Props={{
          placeholder: 'Last Name'
        }}
        style={{ margin: theme.spacing(2, 0) }}
        onSubmit={console.log}
      />
    </ContentBlock>
  );
}

export default function StyleGuide() {
  return (
    <ContentBlock
      margin={[8, 'auto', 4]}
      style={{ overflowX: 'hidden' }}
    >
      <Typography variant="display1">
        Type
      </Typography>
      <Type />

      <Typography variant="display1">
        Buttons
      </Typography>
      <Buttons />

      <Typography variant="display1">
        Text Fields
      </Typography>
      <TextFields />

      <Typography variant="display1">
        Cards
      </Typography>
      <Cards />

      <Typography variant="display1">
        Carousel
      </Typography>
      <Carousels />

      <Typography variant="display1">
        Fancy Inputs
      </Typography>
      <FancyInputs />
    </ContentBlock>
  );
}
